import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    ApplyTemplateResult,
    CatalogDetails,
    CatalogListResponse,
    FilterOptionsResponse,
    GeneratorOption,
    NameOverrideResult,
    StoreBrandDetails,
    StoreBrandListResponse,
    TemplateDetails,
    TemplateListResponse,
    UpdateStoreBrandsResult,
    UpdateTemplatesResult,
} from '../types/reportsAdmin.types';
import { listStates } from './productMasterService';
import apiClient, { getApiErrorMessage } from '../utils/api/ApiClient';

type Envelope<T> = {
    status: string;
    message: string;
    data: T;
};

const PRIMARY_HEADER = { 'X-Store-Brand-Id': '' };

function brandHeader(storeBrandId: string) {
    return { 'X-Store-Brand-Id': storeBrandId };
}

export type ReportsAdminError = {
    message: string;
    code?: string;
    statusCode?: number;
    raw: unknown;
};

const GENERIC_BACKEND_MESSAGES = [
    'an unexpected error occurred',
    'something went wrong',
    'internal server error',
    'no response from server',
    'request failed',
    'please try again later',
];

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

export function parseReportsAdminError(err: unknown): ReportsAdminError {
    if (typeof err === 'string' && err.trim()) {
        return { message: err.trim(), raw: err };
    }
    const root = asRecord(err);
    const payload = asRecord(root?.data);
    const nested = asRecord(payload?.data);
    const messageFromPayload =
        typeof payload?.message === 'string' && payload.message.trim()
            ? payload.message.trim()
            : undefined;
    const code =
        (typeof nested?.code === 'string' && nested.code) ||
        (typeof payload?.code === 'string' && payload.code) ||
        (typeof payload?.errorCode === 'string' && payload.errorCode) ||
        (typeof root?.errorCode === 'string' && root.errorCode) ||
        undefined;
    const statusCode =
        typeof root?.status === 'number'
            ? root.status
            : typeof root?.statusCode === 'number'
              ? root.statusCode
              : undefined;
    return {
        message: messageFromPayload || getApiErrorMessage(err),
        code,
        statusCode,
        raw: err,
    };
}

export function getReportsAdminErrorCode(err: unknown): string | undefined {
    return parseReportsAdminError(err).code;
}

export function getReportsAdminErrorMessage(err: unknown): string {
    return parseReportsAdminError(err).message;
}

export function reportsAdminErrorReason(err: unknown, lockedBody?: string): string | undefined {
    const { message } = parseReportsAdminError(err);
    if (!message.trim()) return undefined;
    const lower = message.toLowerCase();
    if (GENERIC_BACKEND_MESSAGES.some((item) => lower.includes(item))) return undefined;
    if (lockedBody && lockedBody.includes(message)) return undefined;
    return message;
}

async function unwrap<T>(promise: Promise<Envelope<T>>): Promise<T> {
    const response = await promise;
    if (response.status !== 'success') {
        throw {
            message: response.message || 'Request failed',
            data: response,
        };
    }
    return response.data;
}

export async function fetchReportFilterOptions(): Promise<FilterOptionsResponse> {
    return unwrap(
        apiClient.get<Envelope<FilterOptionsResponse>>(API_ENDPOINTS.REPORT.FILTER_OPTIONS, {
            headers: PRIMARY_HEADER,
        })
    );
}

export { listStates };

export async function listCatalog(body: Record<string, unknown>): Promise<CatalogListResponse> {
    return unwrap(
        apiClient.post<Envelope<CatalogListResponse>>(API_ENDPOINTS.REPORT.CATALOG_LIST, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

export async function getCatalogDetails(reportID: string): Promise<CatalogDetails> {
    return unwrap(
        apiClient.post<Envelope<CatalogDetails>>(
            API_ENDPOINTS.REPORT.CATALOG_DETAILS,
            { reportID },
            { headers: PRIMARY_HEADER }
        )
    );
}

export async function createCatalogReport(body: Record<string, unknown>): Promise<CatalogDetails> {
    return unwrap(
        apiClient.post<Envelope<CatalogDetails>>(API_ENDPOINTS.REPORT.CATALOG_CREATE, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

export async function updateCatalogReport(body: Record<string, unknown>): Promise<CatalogDetails> {
    return unwrap(
        apiClient.patch<Envelope<CatalogDetails>>(API_ENDPOINTS.REPORT.CATALOG_UPDATE, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

export async function listGenerators(q?: string): Promise<GeneratorOption[]> {
    const query = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    const data = await unwrap(
        apiClient.get<Envelope<{ items: GeneratorOption[] } | GeneratorOption[]>>(
            `${API_ENDPOINTS.REPORT.CATALOG_GENERATORS}${query}`,
            { headers: PRIMARY_HEADER }
        )
    );
    if (Array.isArray(data)) return data;
    return data.items ?? [];
}

export async function updateCatalogTemplates(body: {
    reportID: string;
    inTemplateIds: string[];
    outTemplateIds: string[];
}): Promise<UpdateTemplatesResult> {
    return unwrap(
        apiClient.post<Envelope<UpdateTemplatesResult>>(
            API_ENDPOINTS.REPORT.CATALOG_UPDATE_TEMPLATES,
            body,
            { headers: PRIMARY_HEADER }
        )
    );
}

export async function updateCatalogStoreBrands(
    body: Record<string, unknown>
): Promise<UpdateStoreBrandsResult> {
    return unwrap(
        apiClient.post<Envelope<UpdateStoreBrandsResult>>(
            API_ENDPOINTS.REPORT.CATALOG_UPDATE_STORE_BRANDS,
            body,
            { headers: PRIMARY_HEADER, timeout: 120000 }
        )
    );
}

export async function listTemplates(body: Record<string, unknown>): Promise<TemplateListResponse> {
    return unwrap(
        apiClient.post<Envelope<TemplateListResponse>>(API_ENDPOINTS.REPORT.TEMPLATES_LIST, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

function asTagLabel(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (value && typeof value === 'object' && 'tag' in value) {
        const tag = (value as { tag?: unknown }).tag;
        return typeof tag === 'string' ? tag.trim() : '';
    }
    return '';
}

export async function listTemplateTags(q?: string): Promise<string[]> {
    const query = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    const data = await unwrap(
        apiClient.get<Envelope<{ tags: unknown[] } | unknown[]>>(
            `${API_ENDPOINTS.REPORT.TEMPLATES_TAGS}${query}`,
            { headers: PRIMARY_HEADER }
        )
    );
    const raw = Array.isArray(data) ? data : (data?.tags ?? []);
    return raw.map(asTagLabel).filter(Boolean);
}

export async function getTemplateDetails(id: string): Promise<TemplateDetails> {
    return unwrap(
        apiClient.post<Envelope<TemplateDetails>>(
            API_ENDPOINTS.REPORT.TEMPLATES_DETAILS,
            { id },
            { headers: PRIMARY_HEADER }
        )
    );
}

export async function createTemplate(body: Record<string, unknown>): Promise<TemplateDetails> {
    return unwrap(
        apiClient.post<Envelope<TemplateDetails>>(API_ENDPOINTS.REPORT.TEMPLATES_CREATE, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

export async function updateTemplate(body: Record<string, unknown>): Promise<TemplateDetails> {
    return unwrap(
        apiClient.patch<Envelope<TemplateDetails>>(API_ENDPOINTS.REPORT.TEMPLATES_UPDATE, body, {
            headers: PRIMARY_HEADER,
        })
    );
}

export async function copyTemplateFromStoreBrand(
    body: Record<string, unknown>
): Promise<TemplateDetails> {
    return unwrap(
        apiClient.post<Envelope<TemplateDetails>>(
            API_ENDPOINTS.REPORT.TEMPLATES_COPY_FROM_STORE_BRAND,
            body,
            { headers: PRIMARY_HEADER }
        )
    );
}

export async function applyTemplateToStoreBrands(
    body: Record<string, unknown>
): Promise<ApplyTemplateResult> {
    return unwrap(
        apiClient.post<Envelope<ApplyTemplateResult>>(
            API_ENDPOINTS.REPORT.TEMPLATES_APPLY_TO_STORE_BRANDS,
            body,
            { headers: PRIMARY_HEADER, timeout: 120000 }
        )
    );
}

export async function listStoreBrands(
    body: Record<string, unknown>
): Promise<StoreBrandListResponse> {
    return unwrap(
        apiClient.post<Envelope<StoreBrandListResponse>>(
            API_ENDPOINTS.REPORT.STORE_BRANDS_LIST,
            body,
            { headers: PRIMARY_HEADER }
        )
    );
}

export async function getStoreBrandDetails(storeBrandID: string): Promise<StoreBrandDetails> {
    return unwrap(
        apiClient.post<Envelope<StoreBrandDetails>>(
            API_ENDPOINTS.REPORT.STORE_BRANDS_DETAILS,
            { storeBrandID },
            { headers: brandHeader(storeBrandID) }
        )
    );
}

export async function updateStoreBrandReports(body: {
    storeBrandID: string;
    reportIds: string[];
}): Promise<unknown> {
    return unwrap(
        apiClient.post<Envelope<unknown>>(API_ENDPOINTS.REPORT.STORE_BRANDS_UPDATE_REPORTS, body, {
            headers: brandHeader(body.storeBrandID),
        })
    );
}

export async function updateStoreBrandNameOverride(body: {
    storeBrandID: string;
    reportID: string;
    nameOverride: string;
}): Promise<NameOverrideResult> {
    return unwrap(
        apiClient.patch<Envelope<NameOverrideResult>>(
            API_ENDPOINTS.REPORT.STORE_BRANDS_NAME_OVERRIDE,
            body,
            { headers: brandHeader(body.storeBrandID) }
        )
    );
}
